import React, { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
	IOfferContext,
	ITypeVignetteOrderingProcessFields,
	OfferContext,
	VignetteContext,
	getHelpDrawerCollection,
} from '@adac-mp/shared';
import { Accordion, ContentLoader, Drawer, getDrawerHelperData, Richtext, Typography } from '@adac-mp/components';
import { ContentfulDrawerContent } from '@adac-mp/components/src/components/ContentfulDrawerContent';

interface IDateSelectionDrawerProps {
	id: string;
	drawerIsOpen: boolean;
	setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DateSelectionDrawer = ({ id, drawerIsOpen, setDrawerIsOpen }: IDateSelectionDrawerProps) => {
	const { contentfulLabels, selectedCountries } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;

	const [searchParams] = useSearchParams();
	const { lang, allOffers } = useContext<IOfferContext>(OfferContext);
	const { loading, data } = useQuery(getHelpDrawerCollection, {
		variables: { lang: lang || 'de', id },
	});

	const drawerContent = data?.helpDrawerCollection.items[0];

	const isSchweizDeactivated = allOffers
		.filter((offer) => offer.supplierId === 14)
		.some((offer) => offer.deactivated);
	const isSchweizAllowed = !isSchweizDeactivated || searchParams.get('enableBAZG') === 'true';

	const allowedSupplierIds = isSchweizAllowed ? [3, 14, 7] : [3, 7];
	const allSuppliers = allowedSupplierIds.filter((suppId) => allOffers.some((offer) => offer.supplierId === suppId));

	const getVignetteHelpDrawerNameBySupplierId = (suppId: number) => {
		switch (suppId) {
			case 3:
				return 'vignetteDate';
			case 7:
				return 'sloveniaVignetteInfo';
			case 14:
				return 'vignetteDateSchweiz';
			default:
				return '';
		}
	};

	const getSectionTollHelpDrawerNameBySupplierId = (suppId: number) => {
		switch (suppId) {
			case 3:
				return 'tollSection';
			default:
				return '';
		}
	};

	const renderVignetteDateInfosBySupplier = (suppId: number) => {
		const helpDrawerId = getVignetteHelpDrawerNameBySupplierId(suppId);
		const helpDrawerData = getDrawerHelperData(helpDrawerId, currentLabels?.helpDrawers);
		const isTheOnlySelectedCountry = Boolean(
			selectedCountries.length === 1 &&
				selectedCountries.find((selectedCountry) => selectedCountry.supplierId === suppId),
		);

		if (!helpDrawerData) return;

		return (
			<Accordion
				preventAnimation
				defaultOpen={Boolean(isTheOnlySelectedCountry)}
				className="py-2.5 px-1.5 md:py-3"
				title="Digitale Vignette"
				chevronColor="#3D3D3D"
				classNameOpen="!max-h-fit">
				<div className="pb-2 pt-[26px] md:pt-4.5 px-3">
					<ContentfulDrawerContent hideHeadline id={helpDrawerData.id} className="!py-0 md:!py-0" />
				</div>
			</Accordion>
		);
	};

	const renderSectionTollDateInfosBySupplier = (suppId: number) => {
		const helpDrawerId = getSectionTollHelpDrawerNameBySupplierId(suppId);
		const helpDrawerData = getDrawerHelperData(helpDrawerId, currentLabels?.helpDrawers);
		const isTheOnlySelectedCountry = Boolean(
			selectedCountries.length === 1 &&
				selectedCountries.find((selectedCountry) => selectedCountry.supplierId === suppId),
		);

		if (!helpDrawerData) return;

		return (
			<Accordion
				preventAnimation
				defaultOpen={isTheOnlySelectedCountry}
				className="py-2.5 px-1.5 md:py-3"
				title="Digitale Streckenmaut"
				chevronColor="#3D3D3D"
				classNameOpen="!max-h-fit">
				<div className="pb-2 pt-[26px] md:pt-4.5 px-3">
					<ContentfulDrawerContent hideHeadline id={helpDrawerData.id} className="!py-0 md:!py-0" />
				</div>
			</Accordion>
		);
	};

	const getRichTextHeadlineBySupplierId = (suppId: number) => {
		switch (suppId) {
			case 3:
				return drawerContent.helpDrawerRichText;
			case 7:
				return drawerContent.helpDrawerThirdText;
			case 14:
				return drawerContent.helpDrawerSecondText;
			default:
				return drawerContent.helpDrawerRichText;
		}
	};

	if (loading || !drawerContent)
		return <ContentLoader isLoading hasPortal={false} height="h-[calc(100dvh_-_69px)]" />;

	return (
		<>
			<Drawer
				title={drawerContent.helpDrawerTitle || 'Gültigkeit, Umbuchung und Stornierung'}
				paddingX="small"
				isOpen={drawerIsOpen}
				setIsOpen={setDrawerIsOpen}>
				{drawerContent?.headline && (
					<Typography className="font-normal py-6 md:py-8" component="h3" color="title" size="h2">
						{drawerContent?.headline ?? ''}
					</Typography>
				)}
				<div className="flex flex-col gap-4">
					{allSuppliers.map((suppId) => (
						<React.Fragment key={suppId}>
							<div className="px-4 pt-6 mb-4 md:pt-8">
								<Typography color="text" size="xlSpace">
									{drawerContent && drawerContent.helpDrawerRichText ? (
										<Richtext document={getRichTextHeadlineBySupplierId(suppId)} />
									) : (
										''
									)}
								</Typography>
							</div>
							<div className="flex flex-col gap-1.5">
								{renderVignetteDateInfosBySupplier(suppId)}
								{suppId === 3 && renderSectionTollDateInfosBySupplier(suppId)}
							</div>
						</React.Fragment>
					))}
				</div>
			</Drawer>
		</>
	);
};
